import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { hr } from "date-fns/locale";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./GapAnaliza.css";
import { ReportAPI } from "../../Api/ReportAPI";

function convertToLocalDate(reportDate) {
  const trimmed = reportDate.trim().replace(/\.$/, "");
  const parts = trimmed.split(".");
  if (parts.length !== 3) return new Date();

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);

  const dateObj = new Date(year, month, day);
  dateObj.setHours(0, 0, 0, 0);
  return dateObj;
}

const GapAnaliza = () => {
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsError, setReportsError] = useState(null);

  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await ReportAPI.getGapReports();
        const parsedReports = response.data || [];

        parsedReports.sort((a, b) => {
          const dateA = convertToLocalDate(a.dateStr).getTime();
          const dateB = convertToLocalDate(b.dateStr).getTime();
          return dateB - dateA;
        });

        setReports(parsedReports);

        if (parsedReports.length > 0) {
          setSelectedCalendarDate(convertToLocalDate(parsedReports[0].dateStr));
        }
      } catch (error) {
        console.error(error);
        setReportsError("Došlo je do pogreške prilikom dohvaćanja gap analiza.");
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, []);

  const availableDates = useMemo(() => {
    const dateSet = new Set();
    reports.forEach((r) => {
      dateSet.add(convertToLocalDate(r.dateStr).getTime());
    });

    return Array.from(dateSet)
      .map((t) => new Date(t))
      .sort((a, b) => b.getTime() - a.getTime());
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (!selectedCalendarDate) return [];

    return reports.filter((r) => {
      return (
        convertToLocalDate(r.dateStr).getTime() ===
        selectedCalendarDate.getTime()
      );
    });
  }, [reports, selectedCalendarDate]);

  // ✅ kad klikneš report → load preview
  const handleReportClick = async (report) => {
    setSelectedReport(report);

    if (report.extension !== "pdf") return;

    try {
      setLoadingPreview(true);

      const res = await ReportAPI.getPreviewFileUrl(report.fileId);

      setPreviewUrl(res.data.url);
    } catch (err) {
      console.error(err);
      setReportsError("Greška pri učitavanju previewa.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadSingle = async (report) => {
    try {
      const response = await ReportAPI.getReportFile(
        report.fileId,
        report.extension === "xlsx" ? 2 : 0
      );

      const mimeType =
        report.extension === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = report.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      setReportsError("Greška kod downloada.");
    }
  };

  const isMobile = window.innerWidth < 768;

  return (
    <div className="container">
      <div className="header">
        <h2>GAP Analiza Keka/Naruđbenice</h2>

        <div className="calendar-container">
          <label>Odaberite datum:</label>
          <DatePicker
            selected={selectedCalendarDate}
            onChange={(date) => setSelectedCalendarDate(date)}
            includeDates={availableDates}
            locale={hr}
            dateFormat="dd MMMM yyyy"
            className="custom-datepicker"
          />
        </div>

        {selectedCalendarDate && (
          <span>{format(selectedCalendarDate, "dd. MMMM yyyy")}</span>
        )}
      </div>

      {loadingReports ? (
        <div className="spinner-container">Loading...</div>
      ) : reportsError ? (
        <div className="error-message">{reportsError}</div>
      ) : filteredReports.length === 0 ? (
        <div>Nema izvještaja</div>
      ) : (
        <>
          {/* LISTA */}
          <div className="date-list">
            {filteredReports.map((report) => (
              <div
                key={report.fileId}
                className="date-item"
                onClick={() => handleReportClick(report)}
              >
                {report.fileName}
              </div>
            ))}
          </div>

          {/* BUTTONS */}
          <div className="buttons-container">
            {selectedReport && (
              <button
                onClick={() => handleDownloadSingle(selectedReport)}
              >
                Preuzmi odabrani
              </button>
            )}
          </div>

          {/* PREVIEW */}
          {selectedReport?.extension === "pdf" && (
            <div className="iframe-container" style={{ marginTop: "20px" }}>
              {loadingPreview ? (
                <div>Učitavanje previewa...</div>
              ) : isMobile ? (
                <a href={previewUrl} target="_blank">
                  Otvori PDF
                </a>
              ) : (
                previewUrl && (
                  <iframe
                    src={previewUrl}
                    width="100%"
                    height="900px"
                    style={{ border: "none" }}
                  />
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GapAnaliza;